/**
 * CampaignTriggerControls (iter135) — master-only trigger + test-mode
 * settings on the active campaign card. Dealer never sees these.
 *
 *   • trigger_mode  : engaged (default) | once_per_session | always
 *   • test_mode     : bookings still work, but the vault is NEVER drawn
 *                     from and rows are flagged as test.
 *
 * Master kill-switch (`master_override_off`) is intentionally kept
 * inside a small "Danger zone" toggle so it isn't hit by accident.
 */
import React, { useState } from "react";
import axios from "axios";
import { AlertTriangle, TestTube2, Zap, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MODES = [
  { value: "engaged", label: "Engaged",
    hint: "Cost-safe production. Requires prior inventory/vehicle view + 25s dwell or exit-intent + 72h cooldown per browser. Only fires on inventory/vehicle pages." },
  { value: "once_per_session", label: "Once per session",
    hint: "One shot per visit — 15s dwell or exit-intent. No engagement requirement, no long cooldown." },
  { value: "always", label: "Always (testing)",
    hint: "Fires on every page load and refresh. No cooldown, no engagement gate. Use ONLY with Test mode ON — otherwise real gift-cards will be spent on every visitor." },
];

export default function CampaignTriggerControls({ campaign, dealerId, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const patch = async (payload) => {
    if (!campaign?.id) return;
    setBusy(true);
    setErr(null);
    try {
      await axios.patch(
        `${API}/admin/dealers/${dealerId}/campaigns/${campaign.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const mode = campaign.trigger_mode || "engaged";
  const testMode = !!campaign.test_mode;
  const overrideOff = !!campaign.master_override_off;
  const dangerous = mode === "always" && !testMode;

  return (
    <div
      className="mt-3 border-t border-red-200 pt-3 space-y-3"
      data-testid="admin-td-trigger-controls"
    >
      <div className="text-[12px] font-semibold text-red-900 flex items-center gap-2">
        <Zap size={13} /> Trigger &amp; test controls
        <span className="text-[10px] font-normal text-red-700/70 ml-auto">
          Master-only · Dealer sees none of this
        </span>
      </div>

      <div className="space-y-1">
        <label className="block text-[11px] font-semibold text-red-900">When does the popup fire?</label>
        <select
          value={mode}
          disabled={busy}
          onChange={(e) => patch({ trigger_mode: e.target.value })}
          className="w-full text-[12px] bg-white border border-red-300 rounded px-2 py-1.5"
          data-testid="admin-td-trigger-mode"
        >
          {MODES.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <p className="text-[10px] text-red-800/80 leading-tight">
          {MODES.find((m) => m.value === mode)?.hint}
        </p>
      </div>

      <div className="flex items-center gap-2 bg-white border border-red-200 rounded px-2 py-2">
        <input
          type="checkbox"
          id={`test-mode-${campaign.id}`}
          checked={testMode}
          disabled={busy}
          onChange={(e) => patch({ test_mode: e.target.checked })}
          className="h-4 w-4"
          data-testid="admin-td-test-mode-toggle"
        />
        <label htmlFor={`test-mode-${campaign.id}`} className="text-[12px] flex-1 cursor-pointer">
          <div className="font-semibold text-red-900 flex items-center gap-1.5">
            <TestTube2 size={12} /> Test mode
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">NON-PROD</span>
          </div>
          <div className="text-[10px] text-red-800/80">
            Renders popup + form works, but no code is drawn from the live vault. Bookings + redemptions flagged as <code>test</code> and excluded from reconciliation.
          </div>
        </label>
      </div>

      {dangerous && (
        <div className="text-[11px] font-semibold bg-red-600 text-white rounded px-2 py-1.5 flex items-center gap-1.5">
          <AlertTriangle size={13} /> Always + Test-mode OFF will spend real gift-card budget on every visitor. Turn Test mode ON.
        </div>
      )}

      <details className="text-[11px]">
        <summary className="cursor-pointer text-red-900 font-semibold">Danger zone — master kill switch</summary>
        <div className="mt-2 flex items-center gap-2 bg-white border border-red-200 rounded px-2 py-2">
          <input
            type="checkbox"
            id={`override-${campaign.id}`}
            checked={overrideOff}
            disabled={busy}
            onChange={(e) => patch({ master_override_off: e.target.checked })}
            className="h-4 w-4"
            data-testid="admin-td-master-override-off"
          />
          <label htmlFor={`override-${campaign.id}`} className="text-[12px] flex-1 cursor-pointer">
            <div className="font-semibold text-red-900">Force popup OFF (master override)</div>
            <div className="text-[10px] text-red-800/80">
              Popup will not render on any dealer site regardless of the dealer&apos;s switch or the trigger mode. Master wins.
            </div>
          </label>
        </div>
      </details>

      {err && <div className="text-[11px] text-red-800 bg-red-100 border border-red-300 rounded p-1.5">{err}</div>}
      {busy && (
        <div className="text-[11px] text-red-700 flex items-center gap-1">
          <Loader2 size={11} className="animate-spin" /> Saving…
        </div>
      )}
    </div>
  );
}
